import { ModalWrapper } from 'components/site/modals/ModalWrapper';
import { DetailQuery_moment } from 'graphql/query/__generated__/DetailQuery';
import { ModalType } from 'libs/types/modal/modalTypes';
import { useIsWide } from 'libs/utils/logic/ui/responsiveLogic';
import { useAnalytics } from 'libs/utils/site/analytics';
import { createContext, useContext, useEffect, useState } from 'react';

interface StoryListData {
    currentIndex: number;
    visibleStoryIds: string[];
}

interface DetailModalData {
    id: string;
    markerId: string | null;
    detail?: DetailQuery_moment;
}

interface DetailListModalData {
    storyIds: string[];
    sort?: 'title' | 'none';
    show?: 'all' | 'connected';
}
export interface ICtxModal {
    modal: ModalType;
    detailModal: DetailModalData;
    detailListModal: DetailListModalData;
    showListOfStories: boolean;
    showFilters: boolean;
    storyList: StoryListData;
    setModal: (modal: ModalType) => void;
    setShowListOfStories: (show: boolean) => void;
    setShowFilters: (show: boolean) => void;
    setStoryList: (data: StoryListData) => void;
    setVisibleStoryIds: (data: string[]) => void;
    setDetailModal: (value: DetailModalData) => void;
    setDetailListModal: (value: DetailListModalData) => void;
    closeAll: () => void;
}

export const CtxModal = createContext<ICtxModal>(null);

export const ModalStateProvider = ({ children }: { children: React.ReactNode }) => {
    const [modal, setModal] = useState<ModalType>(null);
    const [detailModal, setDetailModal] = useState<DetailModalData>(null);
    const [detailListModal, setDetailListModal] = useState<DetailListModalData>(null);
    const [storyList, setStoryList] = useState<StoryListData>(null);
    const [showListOfStories, setShowListOfStories] = useState<boolean>(false);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const analytics = useAnalytics();
    const isWide = useIsWide();

    useEffect(() => {
        if (detailModal?.detail?.storyId) {
            analytics.track('detail.opened', {
                storyId: detailModal.detail.storyId,
                storyTitle: detailModal.detail.header?.title
            });
        }
    }, [detailModal?.detail?.storyId]);

    useEffect(() => {
        if (showListOfStories) {
            analytics.track('sidebar_list.opened');
        }
    }, [showListOfStories]);

    useEffect(() => {
        if (showFilters) {
            analytics.track('sidebar_filters.opened');
        }
    }, [showFilters]);

    useEffect(() => {
        if (!isWide) {
            if (detailModal) {
                const scrollY = document.documentElement.style.getPropertyValue('--scroll-y');
                const body = document.body;
                body.style.position = 'fixed';
                body.style.top = `-${scrollY}`;
            } else {
                const body = document.body;
                const scrollY = body.style.top;
                body.style.position = '';
                body.style.top = '';
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        }
    }, [detailModal]);

    const setVisibleStoryIds = (data: string[]) => {
        const index = data.findIndex((e) => e === detailModal?.id);
        if (index === -1) {
            // Close the modal in case a filter is applied but the current detail is not in the list
            // but still update the visibleStoryIds so it's prepared
            setDetailModal(null);
            setStoryList({
                currentIndex: 0,
                visibleStoryIds: data
            });
        } else {
            // Otherwise do nothing really and update the visibleStoryIds
            setStoryList({
                currentIndex: index,
                visibleStoryIds: data
            });
        }
    };

    const closeAll = () => {
        setShowListOfStories(false);
        setShowFilters(false);
        setDetailModal(null);
        setDetailListModal(null);
    };

    // Control closing of other modals when one is open
    useEffect(() => {
        if (showListOfStories) {
            setDetailModal(null);
            setShowFilters(false);
            setDetailListModal(null);
        }
    }, [showListOfStories]);

    useEffect(() => {
        if (showFilters) {
            setShowListOfStories(false);
            setDetailModal(null);
        }
    }, [showFilters]);

    useEffect(() => {
        if (detailModal?.id) {
            setShowFilters(false);
        }
    }, [detailModal]);

    useEffect(() => {
        if (detailListModal) {
            setDetailModal(null);
            setShowFilters(false);
            setShowListOfStories(false);
        }
    }, [detailListModal]);

    return (
        <CtxModal.Provider
            value={{
                modal,
                setModal,
                detailModal,
                setDetailModal,
                setDetailListModal: (p) => {
                    setDetailListModal(p);
                },
                detailListModal,
                storyList,
                setStoryList,
                setVisibleStoryIds,
                showListOfStories,
                setShowListOfStories,
                showFilters,
                setShowFilters,
                closeAll
            }}
        >
            {children}
        </CtxModal.Provider>
    );
};
interface Props {
    className?: string;
}

export const ModalStateContent = (props: Props) => {
    const ctxModal = useContext(CtxModal);
    return (
        <div className={props.className}>
            <>{ctxModal.modal && <ModalWrapper />}</>
        </div>
    );
};
